import { PROTOCOL_TEST_IDS } from '@/components/admin/protocoles/contracts';
import { RangementPanel } from '@/components/admin/protocoles/rangement/rangement-panel';
import { VariablePalette } from '@/components/admin/protocoles/step/variable-palette';
import { PaintedTextField } from '@/components/admin/variables/painted-text-field';
import {
  RANGEMENT_ORPHAN_WARNING,
  readingPaletteEmpty,
  readingPaletteGroups,
} from '@/lib/analyst-reading';
import { firstPaletteItem, protocolVariableDescriber } from '@/lib/protocol-step-draft';
import {
  RANGEMENT_CONSIGNE_MAX,
  RANGEMENT_ONE_PASSAGE_HELP,
  RANGEMENT_ONE_PASSAGE_LABEL,
  RANGEMENT_PALETTE_FULL,
  RANGEMENT_REFUSAL_LINE,
  rangementRefusalSentence,
  targetGroups,
  toggledTarget,
} from '@/lib/rangement-editor';
import { RANGEMENT_PLACE } from '@/lib/variable-pill';
import type { VariableZone } from '@/lib/variable-pill';
import type { ProtocolRangement, ProtocolStepDraft } from '@matter_extractor/shared';
import {
  rangementConsigneOrphan,
  rangementProblems,
  variableToken,
} from '@matter_extractor/shared';
import { TriangleAlert } from 'lucide-react';
import { useId, useMemo, useState } from 'react';

/**
 * La vue « Rangement » de l'éditeur de protocole : les objectifs cibles et la
 * consigne à gauche, « Ce que le rangeur recevra » à droite (scenario-succes.md
 * §4, issue #177 ; maquette `EditeurRangement`), sur le patron de
 * `reading/analyst-reading-field.tsx`, le champ peint de chaque lecture de la
 * section Analyste.
 *
 * ## The targets: every objective of the protocol, ticked by step
 *
 * A fieldset of checkboxes grouped by step, in the order of the protocol -
 * every objective, named or not yet (`targetGroups`). The ticks are the draft's
 * `rangement.targetObjectiveIds`, rewritten by `toggledTarget` in the order of
 * the protocol rather than of the ticking, so the draft never holds a list the
 * save would reorder. None ticked: no rangement.
 *
 * ## « Un passage ne sert qu'un objectif » (relecture.md §12, issue #187)
 *
 * One checkbox under the targets - `rangement.oneObjectivePerPassage`, unticked
 * by default, so a version written before it keeps the rule it had - and its
 * line of help. It rides the draft like the ticks: `{ ...rangement, … }` is the
 * only way this view writes the section, so the setting survives every other
 * edit. What ticking does to the contract the ranger receives is drawn by the
 * panel on the right, from `shared`, not written here.
 *
 * ## The consigne is the reading's field, for another reader
 *
 * The PAINTED FIELD of the directions, with the reading's palette
 * (`readingPaletteGroups`: every named objective, by step, no criterion) and
 * the reading's describer (objectives only): `@` and the `{ }` button cite an
 * objective, stored as `{{objectif:id}}`, drawn as a pill under the objective's
 * CURRENT name, resolved to that name for the rangeur. The zone stands at
 * `RANGEMENT_PLACE`, so a pill's fiche says « servi au rangeur ». Bounded at
 * 6000 characters of STORED text and REFUSED past it, never cut, the refusal
 * said in an `<output>` that stays mounted - the reading's régime.
 *
 * ## The refusals are said where they are fixed
 *
 * The two of §4 are `rangementProblems`, the pure function the server runs at
 * the save: the consigne missing is said under the consigne, the targets
 * missing under the list of targets. The pill « N problèmes » and the save box
 * say them as well, in one line. An orphan chip - a citation of an objective the
 * version no longer carries - is a WARNING under the consigne, and the pill is
 * grey (the reading's fiche): the rangeur reads « (objectif retiré) ».
 *
 * It rides the autosaved draft like every other field: `onChange` is the
 * layer's `onDraft`. Read-only (« Voir », a retired protocol): nothing ticks,
 * nothing types, and the panel still draws what was served.
 */

export interface RangementViewProps {
  /** The section as typed - the draft's, or the version « Voir » is showing. */
  readonly rangement: ProtocolRangement;
  /** The steps of that same content: what is ticked, cited and resolved on. */
  readonly steps: readonly ProtocolStepDraft[];
  /** « Voir », or a retired protocol: nothing is typable. */
  readonly readOnly: boolean;
  readonly onChange: (next: ProtocolRangement) => void;
}

export function RangementView({ rangement, steps, readOnly, onChange }: RangementViewProps) {
  const targetsId = useId();
  const targetsHelpId = useId();
  const targetsLineId = useId();
  const labelId = useId();
  const guideId = useId();
  const hintId = useId();
  const consigneLineId = useId();
  const onePassageHelpId = useId();
  const groups = targetGroups(steps, rangement);
  const problems = rangementProblems({ steps, rangement });
  const consigneMissing = problems.some((problem) => problem.kind === 'rangement-consigne-missing');
  const targetsMissing = problems.some((problem) => problem.kind === 'rangement-targets-missing');
  const orphan = rangementConsigneOrphan(steps, rangement.consigne);
  /** The last refusal of the bound, with the consigne it was said on. */
  const [refusal, setRefusal] = useState<{
    readonly at: string;
    readonly sentence: string;
  } | null>(null);
  const consigneDescribedBy = [
    guideId,
    readOnly ? null : hintId,
    consigneMissing || orphan ? consigneLineId : null,
  ]
    .filter((id): id is string => id !== null)
    .join(' ');

  // Memoised on the steps, for the reason `analyst-reading-field.tsx` gives: the
  // painted field repaints when its describer's answers change. The criteria
  // are an empty list ON PURPOSE - only objectives are cited here.
  const zone = useMemo<VariableZone>(
    () => ({
      describe: protocolVariableDescriber(steps, []),
      place: RANGEMENT_PLACE,
      experienceId: null,
    }),
    [steps],
  );

  return (
    <div
      data-testid={PROTOCOL_TEST_IDS.rangement}
      className="grid items-start gap-6 lg:grid-cols-2"
    >
      <div className="flex min-w-0 flex-col gap-5">
        <fieldset
          aria-describedby={`${targetsHelpId}${targetsMissing ? ` ${targetsLineId}` : ''}`}
          className="flex min-w-0 flex-col gap-2 border-0 p-0"
        >
          <legend id={targetsId} className="font-semibold text-gray-800 text-sm">
            Objectifs cibles
          </legend>
          <p id={targetsHelpId} className="text-gray-600 text-xs leading-4">
            Les objectifs que le rangeur remplit en relisant toute la conversation. Sur eux,
            l’analyste lit le rangement ; sur les autres, le scribe. Aucun coché : pas de rangement.
          </p>
          <div
            data-testid={PROTOCOL_TEST_IDS.rangementTargets}
            className="flex flex-col gap-2.5 rounded-md border-2 border-gray-200 px-3.5 py-3"
          >
            {groups.length === 0 ? (
              <p className="text-gray-600 text-sm">
                Aucun objectif dans ce protocole pour l’instant.
              </p>
            ) : (
              groups.map((group) => (
                <div key={group.stepId} className="flex flex-col gap-1">
                  <p className="font-semibold text-gray-600 text-xs uppercase tracking-wide">
                    {group.title}
                  </p>
                  <div className="grid gap-x-4 gap-y-1 sm:grid-cols-2">
                    {group.options.map((option) => (
                      <label
                        key={option.id}
                        className="flex min-h-7 min-w-0 cursor-pointer items-center gap-2.5 text-gray-800 text-sm has-[:disabled]:cursor-default"
                      >
                        <input
                          type="checkbox"
                          data-testid={PROTOCOL_TEST_IDS.rangementTarget}
                          data-objective-id={option.id}
                          checked={option.checked}
                          disabled={readOnly}
                          onChange={(event) =>
                            onChange(
                              toggledTarget(
                                steps,
                                rangement,
                                option.id,
                                event.currentTarget.checked,
                              ),
                            )
                          }
                          className="size-4.5 shrink-0 accent-bleu-nuit"
                        />
                        <span
                          className={
                            option.name.trim() === ''
                              ? 'min-w-0 text-gray-500 italic'
                              : 'min-w-0 break-words'
                          }
                        >
                          {option.name.trim() === '' ? 'Objectif sans nom' : option.name}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
          {targetsMissing ? (
            <RangementLine
              id={targetsLineId}
              kind="rangement-targets-missing"
              sentence={RANGEMENT_REFUSAL_LINE['rangement-targets-missing']}
            />
          ) : null}
        </fieldset>

        <div className="flex min-w-0 flex-col gap-1">
          <label className="flex min-h-7 w-fit max-w-full cursor-pointer items-center gap-2.5 font-semibold text-gray-800 text-sm has-[:disabled]:cursor-default">
            <input
              type="checkbox"
              data-testid={PROTOCOL_TEST_IDS.rangementOnePassage}
              checked={rangement.oneObjectivePerPassage}
              disabled={readOnly}
              aria-describedby={onePassageHelpId}
              onChange={(event) =>
                onChange({ ...rangement, oneObjectivePerPassage: event.currentTarget.checked })
              }
              className="size-4.5 shrink-0 accent-bleu-nuit"
            />
            <span className="min-w-0">{RANGEMENT_ONE_PASSAGE_LABEL}</span>
          </label>
          <p id={onePassageHelpId} className="text-gray-600 text-xs leading-4">
            {RANGEMENT_ONE_PASSAGE_HELP}
          </p>
        </div>

        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex flex-col gap-0.5">
            <p id={labelId} className="font-semibold text-gray-800 text-sm">
              Consigne du rangeur
            </p>
            <p id={guideId} className="text-gray-600 text-xs leading-4">
              Comment ranger la matière sur les objectifs cibles. Le cadre, la conversation et le
              contrat de sortie restent en code. Cite les objectifs avec @.
            </p>
          </div>
          <PaintedTextField
            value={rangement.consigne}
            onChange={(consigne) => onChange({ ...rangement, consigne })}
            maxLength={RANGEMENT_CONSIGNE_MAX}
            onRefused={(refused) =>
              setRefusal({ at: rangement.consigne, sentence: rangementRefusalSentence(refused) })
            }
            readOnly={readOnly}
            placeholder="Dites au rangeur comment ranger la matière sur les objectifs cibles : quel passage va dans quel objectif, ce qu’il laisse de côté."
            ariaLabelledBy={labelId}
            ariaDescribedBy={consigneDescribedBy}
            testId={PROTOCOL_TEST_IDS.rangementConsigne}
            insertTestId={PROTOCOL_TEST_IDS.rangementInsertVariable}
            minHeightClass="min-h-60"
            className="flex min-h-0 flex-col"
            zone={zone}
            renderMenu={(query, insert, fits) => (
              <VariablePalette
                groups={readingPaletteGroups(steps, query ?? '')}
                query={query}
                empty={readingPaletteEmpty(steps, query ?? '')}
                testId={PROTOCOL_TEST_IDS.rangementPalette}
                fits={(variable) => fits(variableToken(variable))}
                full={RANGEMENT_PALETTE_FULL}
                onPick={(variable) => insert(variableToken(variable))}
              />
            )}
            firstToken={(query) => {
              const first = firstPaletteItem(readingPaletteGroups(steps, query));
              return first === null ? null : variableToken(first.variable);
            }}
          />
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 space-y-1">
              {readOnly ? null : (
                <p id={hintId} className="text-gray-600 text-xs leading-4">
                  Tape <span className="font-semibold">@</span> ou le bouton{' '}
                  <span className="font-semibold">{'{ }'}</span> pour citer un objectif : le rangeur
                  lira son nom. Une citation suit son objectif si on le renomme.
                </p>
              )}
              {/* An `<output>` (role `status`), mounted while the field is typable and EMPTY
                  until a refusal: a live region that appears with its text is one some
                  screen readers never announce. */}
              {readOnly ? null : (
                <output
                  data-testid={PROTOCOL_TEST_IDS.rangementRefused}
                  className="block font-semibold text-rouge-deep text-xs leading-4"
                >
                  {refusal !== null && refusal.at === rangement.consigne ? refusal.sentence : ''}
                </output>
              )}
            </div>
            {/* Hidden from assistive technology: a count re-read at every keystroke is noise. */}
            <p aria-hidden="true" className="shrink-0 text-right text-gray-600 text-xs leading-4">
              {rangement.consigne.length} / {RANGEMENT_CONSIGNE_MAX} caractères
            </p>
          </div>
          <div id={consigneLineId} className="flex flex-col gap-1.5 empty:hidden">
            {consigneMissing ? (
              <RangementLine
                kind="rangement-consigne-missing"
                sentence={RANGEMENT_REFUSAL_LINE['rangement-consigne-missing']}
              />
            ) : null}
            {orphan ? (
              <RangementLine kind="rangement-variable-orphan" sentence={RANGEMENT_ORPHAN_WARNING} />
            ) : null}
          </div>
        </div>
      </div>

      <RangementPanel rangement={rangement} steps={steps} />
    </div>
  );
}

/** The yellow line of a refusal or of an orphan chip, said under the field it concerns. */
function RangementLine({
  id,
  kind,
  sentence,
}: {
  id?: string;
  kind: 'rangement-consigne-missing' | 'rangement-targets-missing' | 'rangement-variable-orphan';
  sentence: string;
}) {
  return (
    <p
      id={id}
      data-testid={PROTOCOL_TEST_IDS.rangementWarning}
      data-kind={kind}
      className="flex items-start gap-2 rounded-md border border-gray-200 border-l-4 border-l-jaune-deep bg-jaune-tint px-3 py-2 font-semibold text-gray-900 text-xs leading-4"
    >
      <TriangleAlert aria-hidden className="mt-px size-3.5 shrink-0 text-jaune-deep" />
      <span className="min-w-0">{sentence}</span>
    </p>
  );
}
